import React, { useEffect, useState } from "react";
import ViewTable from "../../../Components/Common/ViewTable";
import { get, post, put, del, api } from "../../../utils/api";
import Cards from "../../../Components/Common/Cards";
import FilterModal from "../../../Components/Common/FilterModal";
import ActionButtons from "../../../Components/Common/ActionButtons";
import ViewData from "./ViewData";
import Form from "./Form";
import { Alert, Snackbar } from "@mui/material";
import DeleteAlertDialog from "../../../Components/Masters/DeleteAlertDialog";
import BulkUploadModal from "../../../Components/Common/BulkUploadModal";
import BulkUploadReview from "./BulkUploadReview";
import { useUser } from "../../../context/AuthContext";
import {
  shouldShowFacilityColumn,
  withFacilityViewTableHeader,
  mapTableDataWithFacility,
} from "../../../utils/facilityColumn";
import BulkUploadSkippedDialog from "../../../Components/Common/BulkUploadSkippedDialog";

/** Columns in the EOL bulk template — skipped-entry Excel matches these only. */
const EOL_SKIPPED_DOWNLOAD_COLUMNS = [
  "Product Id",
  "Production Month (MM/YYYY)",
  "Number of Units Sold",
  "Data Source",
  "Remark",
];

const productMasterFromItem = (item) =>
  item?.productMaster && typeof item.productMaster === "object"
    ? item.productMaster
    : null;

const eolProductIdLabel = (item) => {
  const pm = productMasterFromItem(item);
  return String(pm?.productId ?? item?.productId ?? "").trim();
};

const eolProductNameLabel = (item) => {
  const pm = productMasterFromItem(item);
  return String(pm?.productName ?? item?.productName ?? "").trim();
};

const eolTreatmentMethodLabel = (item) => {
  const pm = productMasterFromItem(item);
  return String(
    pm?.disposalMethod ?? item?.endOfLifeTreatmentMethod ?? ""
  ).trim();
};

const eolAverageWeightLabel = (item) => {
  const pm = productMasterFromItem(item);
  const w =
    pm?.averageWeightPerUnit ?? pm?.weight ?? item?.averageWeightPerUnit;
  if (w === undefined || w === null || w === "") return "";
  return String(w);
};

const EOLTreatmentOfSoldProducts = () => {
  const { user } = useUser();
  const showFacilityColumn = shouldShowFacilityColumn(user);
  const [sortConfig, setSortConfig] = useState({ key: "", direction: "" });
  const [tableData, setTableData] = useState([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editModal, setEditModal] = useState(false);
  const [viewModal, setViewModal] = useState(false);
  const [editData, setEditData] = useState({});
  const [deleteDialog, setDeleteDialog] = useState(false);
  const [filterObj, setFilterObj] = useState({});
  const [deletedItemId, setDeletedItemId] = useState("");
  const [isFilterModalOpen, setIsFilterModalOpen] = useState(false);
  const [bulkUploadModal, setBulkUploadModal] = useState(false);
  const [excelData, setExcelData] = useState(null);
  const [bulkUploadTable, setBulkUploadTable] = useState(false);
  const [formDataBulkUpload, setFormDataBulkUpload] = useState([]);
  const [matchData, setMatchData] = useState([]);
  const [skippedUploadDialog, setSkippedUploadDialog] = useState({
    open: false,
    skippedEntries: [],
    inserted: 0,
    skipped: 0,
  });
  const [masterProducts, setMasterProducts] = useState([]);

  const [filters, setFilters] = useState({
    productId: [],
    productName: [],
    endOfLifeTreatmentMethod: [],
  });

  const [cardData, setCardData] = useState({
    totalRecords: 0,
    topProductName: [],
    disposalMethodQuantities: {
      quantityByIncineration: 0,
      quantityByRecycling: 0,
      quantityByLandfilling: 0,
    },
  });

  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  const [snackOpen, setsnackOpen] = useState({
    open: false,
    message: "",
    severity: "",
  });

  const headers = withFacilityViewTableHeader([
    { key: "createdAt", label: "Date", sortable: true, width: "12%" },
    { key: "eolMonth", label: "Production Month", sortable: true, width: "15%" },
    { key: "productId", label: "Product Id", sortable: true, width: "13%" },
    { key: "productName", label: "Product Name", sortable: true, width: "18%" },
    {
      key: "endOfLifeTreatmentMethod",
      label: "EOL Treatment Method",
      sortable: true,
      width: "17%",
    },
    { key: "numberOfUnitsSold", label: "Number of Units Sold", sortable: true, width: "15%" },
  ], showFacilityColumn);

  const inputData = [
    { fieldName: "productId", title: "Product Id" },
    { fieldName: "productName", title: "Product Name" },
    { fieldName: "eolMonth", title: "Production Month" },
    {
      fieldName: "endOfLifeTreatmentMethod",
      title: "End-of-Life Treatment Method",
    },
    {
      fieldName: "averageWeightPerUnit",
      title: "Average Weight per Unit (MT)",
    },
    { fieldName: "numberOfUnitsSold", title: "Number of Units Sold" },
    { fieldName: "soldUnit", title: "Sold Unit" },
    { fieldName: "remark", title: "Remark (optional)" },
    { fieldName: "editRemarkReason", title: "Edit Remark Reason" },
  ];

  const filterData = [
    { title: "Date", type: "date", key: "createdAt", value: [] },
    { title: "Production Month", type: "month", key: "eolMonth", value: [] },
    {
      title: "Product Id",
      type: "option",
      key: "productId",
      value: filters?.productId || [],
    },
    {
      title: "Product Name",
      type: "option",
      key: "productName",
      value: filters?.productName || [],
    },
    {
      title: "EOL Treatment Method",
      type: "option",
      key: "endOfLifeTreatmentMethod",
      value: filters?.endOfLifeTreatmentMethod || [],
    },
  ];

  const bannerData = [
    {
      title: "Quantity of waste treated by incineration",
      description: `${
        cardData?.disposalMethodQuantities?.quantityByIncineration ?? 0
      } MT`,
    },
    {
      title: "Quantity of waste treated by recycling",
      description: `${
        cardData?.disposalMethodQuantities?.quantityByRecycling ?? 0
      } MT`,
    },
    {
      title: "Quantity of waste treated by landfilling",
      description: `${
        cardData?.disposalMethodQuantities?.quantityByLandfilling ?? 0
      } MT`,
    },
  ];

  const fetchExcelData = async () => {
    try {
      const res = await get(
        `/master/product/productsList?page=1&limit=1000&sortField=createdAt&sortOrder=desc`
      );
      const products = Array.isArray(res?.data?.data) ? res.data.data : [];

      const template = products.length
        ? products.map((item) => ({
            "Product Id": item?.productId || "",
            "Production Month (MM/YYYY)": "",
            "Number of Units Sold": "",
            Remark: "",
          }))
        : [
            {
              "Product Id": "",
              "Production Month (MM/YYYY)": "",
              "Number of Units Sold": "",
              Remark: "",
            },
          ];

      setExcelData(template);
    } catch (error) {
      console.error("Error generating EOL bulk upload template:", error);
      setExcelData([
        {
          "Product Id": "",
          "Production Month (MM/YYYY)": "",
          "Number of Units Sold": "",
          Remark: "",
        },
      ]);
    }
  };

  const uploadErrorClose = () => {
    setBulkUploadTable(true);
  };

  const handleCloseBulkUpload = () => {
    setBulkUploadModal(false);
    setBulkUploadTable(false);
    setMatchData([]);
    setFormDataBulkUpload([]);
    fetchData();
    fetchCardData();
  };

  const fetchData = async () => {
    try {
      const formatMonthYear = (value) => {
        if (!value) return "";
        const date = new Date(value);
        if (Number.isNaN(date.getTime())) return "";
        return date.toLocaleString("en-US", { month: "short", year: "numeric" });
      };

      const filter = { ...filterObj };

      if (filter.createdAt) {
        filter.startDate = filter?.createdAt?.start;
        filter.endDate = filter?.createdAt?.end || new Date();
        filter.dateField = "createdAt";
        delete filter.createdAt;
      }
      if (filter.eolMonth?.start) {
        filter.startMonth = filter.eolMonth.start;
      }
      if (filter.eolMonth?.end) {
        filter.endMonth = filter.eolMonth.end;
      }
      delete filter.eolMonth;

      const queryParams = new URLSearchParams({
        ...filter,
        page: currentPage,
        limit: 10,
        sortField: sortConfig.key,
        sortOrder: sortConfig.direction,
      }).toString();

      const res = await get(
        `/scope3/eolTreatmentOfSoldProducts/get?${queryParams}`
      );

      const arr =
        res?.data?.data?.map((item) => ({
          ...item,
          eolMonth: formatMonthYear(item?.eolMonth),
          action: "View",
          id: item?._id,
          productId: eolProductIdLabel(item) || item?.productId,
          productName: eolProductNameLabel(item) || item?.productName,
          endOfLifeTreatmentMethod:
            eolTreatmentMethodLabel(item) || item?.endOfLifeTreatmentMethod,
          averageWeightPerUnit:
            eolAverageWeightLabel(item) || item?.averageWeightPerUnit,
        })) || [];

      setTableData(arr);
      setTotalPages(res?.data?.totalPages || 1);
    } catch (error) {
      console.error(error);
    }
  };

  useEffect(() => {
    fetchData();
  }, [sortConfig, filterObj, currentPage]);

  const fetchCardData = async () => {
    try {
      const res = await get(`/scope3/eolTreatmentOfSoldProducts/cardData`);
      const data = res?.data?.data || {};
      setCardData({
        totalRecords: data?.totalRecords || 0,
        topProductName: data?.topProductName || [],
        disposalMethodQuantities: data?.disposalMethodQuantities || {
          quantityByIncineration: 0,
          quantityByRecycling: 0,
          quantityByLandfilling: 0,
        },
      });
    } catch (error) {
      console.error(error);
    }
  };

  const fetchFilters = async () => {
    try {
      const res = await get(`/scope3/eolTreatmentOfSoldProducts/filter`);
      const { productId, productName, endOfLifeTreatmentMethod } =
        res?.data?.data || {};

      setFilters({
        productId: productId || [],
        productName: productName || [],
        endOfLifeTreatmentMethod: endOfLifeTreatmentMethod || [],
      });
    } catch (error) {
      console.error(error);
    }
  };

  useEffect(() => {
    fetchCardData();
  }, []);

  useEffect(() => {
    fetchFilters();
  }, []);

  useEffect(() => {
    fetchExcelData();
  }, []);

  const handleSort = (key, direction) => {
    setSortConfig({ key, direction });
  };

  const handlePageChange = (newPage) => {
    setCurrentPage(newPage);
  };

  const handleFilterChange = (newFilterObj) => {
    setFilterObj(newFilterObj);
    setCurrentPage(1);
  };

  const fetchMasterProducts = async () => {
    try {
      const queryParams = new URLSearchParams({
        limit: "200",
        page: "1",
      }).toString();
      const res = await get(`/master/product/getAll?${queryParams}`);
      setMasterProducts(Array.isArray(res?.data?.data) ? res.data.data : []);
    } catch (error) {
      console.error("Error fetching product masters (EOL):", error);
      setMasterProducts([]);
    }
  };

  useEffect(() => {
    void fetchMasterProducts();
  }, []);

  const openModal = (type, dataForEdit) => {
    void fetchMasterProducts();
    if (type === "add") {
      setIsModalOpen(true);
      setEditModal(false);
      setEditData({});
    } else if (type === "edit") {
      setIsModalOpen(true);
      setEditData(dataForEdit);
      setEditModal(true);
      setViewModal(false);
    }
  };

  const handleEdit = (data) => {
    openModal("edit", data);
  };

  const openViewModal = (data) => {
    setEditData(data);
    setViewModal(true);
  };

  const closeModal = (type) => {
    if (type === "add") {
      setIsModalOpen(false);
    } else if (type === "edit") {
      setIsModalOpen(false);
      setEditModal(false);
      setEditData({});
    }
  };

  const handleSubmit = async (data, isEditing) => {
    try {
      const url = isEditing
        ? `/scope3/eolTreatmentOfSoldProducts/update?Id=${
            editData.id || editData._id
          }`
        : `/scope3/eolTreatmentOfSoldProducts/add`;

      const apiCall = isEditing ? put : post;

      const matchedMaster = masterProducts.find(
        (m) => String(m?.productId || "").trim() === String(data?.productId || "").trim()
      );
      const resolvedProductMaster =
        (data?.productMaster && String(data.productMaster).trim()) ||
        (matchedMaster?._id != null ? String(matchedMaster._id) : "") ||
        (matchedMaster?.id != null ? String(matchedMaster.id) : "") ||
        "";

      if (!isEditing && !resolvedProductMaster) {
        setsnackOpen({
          open: true,
          message: "Product master is required. Select a product from the master list.",
          severity: "error",
        });
        return;
      }

      const payload = {
        eolMonth: data.eolMonth,
        numberOfUnitsSold: data.numberOfUnitsSold,
        soldUnit: data.soldUnit,
        remark: data.remark,
        editRemarkReason: data.editRemarkReason,
        requestStatus: data.requestStatus,
        requestMode: data.requestMode,
        approvalStatus: data.approvalStatus,
        documents: Array.isArray(data.documents) ? data.documents : [],
      };

      if (resolvedProductMaster) {
        payload.productMaster = resolvedProductMaster;
      }

      const res = await apiCall(url, payload);

      if (res) {
        setsnackOpen({
          open: true,
          message: res?.data?.message,
          severity: "success",
        });

        setIsModalOpen(false);

        if (isEditing) {
          setEditModal(false);
          setEditData({});
        }

        fetchCardData();
        fetchData();
      }
    } catch (error) {
      setsnackOpen({
        open: true,
        message: error?.response?.data?.message || "Something went wrong",
        severity: "error",
      });

      console.error("Error in handleSubmit (EOLTreatmentOfSoldProducts):", error);
    }
  };

  const handleDeleteOpen = (row) => {
    setDeletedItemId(row);
    setDeleteDialog(true);
  };

  const handleDeleteAgree = async () => {
    try {
      await del(`/scope3/eolTreatmentOfSoldProducts/delete`, {
        params: {
          Id: deletedItemId?._id,
        },
      });
      setDeleteDialog(false);
      setsnackOpen({
        open: true,
        message: "Deleted successfully",
        severity: "success",
      });
      fetchData();
      fetchCardData();
    } catch (err) {
      console.error(err);
      setsnackOpen({
        open: true,
        message: err.response?.data?.message ?? "Network Error",
        severity: "error",
      });
    }
  };

  return (
    <>
      <Cards
        data={bannerData}
        title={"End of Life Treatment of Sold Products"}
      />

      <ActionButtons
        onFilterClick={() => setIsFilterModalOpen(true)}
        onAddClick={() => openModal("add")}
        filterObj={filterObj}
        onUploadClick={() => setBulkUploadModal(true)}
      />

      <ViewTable
        title={"EOL Treatment"}
        editIcon={user?.companyAdmin ? "disable" : "enable"}
        headers={headers}
        data={mapTableDataWithFacility(tableData, showFacilityColumn)}
        onSort={handleSort}
        sortConfig={sortConfig}
        page={currentPage}
        count={10}
        totalPages={totalPages}
        onPageChange={handlePageChange}
        actionColumnWidthPercent={10}
        onView={(row) => {
          openViewModal(row);
        }}
        onEdit={(row) => openModal("edit", row)}
        onDelete={(row) => handleDeleteOpen(row)}
      />

      <Snackbar
        open={snackOpen.open}
        autoHideDuration={3000}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
        onClose={() => setsnackOpen({ ...snackOpen, open: false })}
      >
        <Alert
          onClose={() => setsnackOpen({ ...snackOpen, open: false })}
          severity={snackOpen.severity}
          variant="filled"
          sx={{ width: "100%" }}
        >
          {snackOpen.message}
        </Alert>
      </Snackbar>

      <Form
        isOpen={isModalOpen}
        onClose={() => closeModal(editModal ? "edit" : "add")}
        onSubmit={handleSubmit}
        header={
          editModal
            ? "Edit End of Life Treatment of Sold Product"
            : "Add End of Life Treatment of Sold Product"
        }
        initialData={editData}
        isEditing={editModal}
      />

      <ViewData
        id={editData ? editData.id || editData._id : ""}
        element={editData}
        title={"EOL Treatment Details"}
        viewData={viewModal}
        setViewData={setViewModal}
        setData={setEditData}
        inputData={inputData}
        handleEdit={handleEdit}
        companyAdmin={user?.companyAdmin}
      />

      {deleteDialog && (
        <DeleteAlertDialog
          {...{
            deleteDialog,
            setDeleteDialog,
            method: "delete",
            id: deletedItemId?._id,
            url: `${api}/scope3/eolTreatmentOfSoldProducts/delete`,
            setsnackOpen,
            fetchData,
            fetchCardData,
          }}
        />
      )}

      <FilterModal
        isOpen={isFilterModalOpen}
        onClose={() => setIsFilterModalOpen(false)}
        filters={filterData}
        onFilterChange={handleFilterChange}
        title="Filter"
      />

      {bulkUploadModal && (
        <BulkUploadModal
          onCloseBulkUpload={handleCloseBulkUpload}
          isBulkUploadModal={bulkUploadModal}
          excelData={excelData}
          fileName={"eolTreatmentOfSoldProducts"}
          setsnackOpen={setsnackOpen}
          url={"/scope3/eolTreatmentOfSoldProducts/bulkupload"}
          setMatchData={setMatchData}
          setFormDataBulkUpload={setFormDataBulkUpload}
          matchedKey="productStatusList"
          errorClose={uploadErrorClose}
          setBulkUploadModal={setBulkUploadModal}
        />
      )}

      {bulkUploadTable && (
        <BulkUploadReview
          bulkUploadTable={bulkUploadTable}
          matchData={matchData}
          setMatchData={setMatchData}
          formDataBulkUpload={formDataBulkUpload}
          setFormDataBulkUpload={setFormDataBulkUpload}
          setsnackOpen={setsnackOpen}
          bulkUploadUrl={"/scope3/eolTreatmentOfSoldProducts/jsonupload"}
          onClose={() => setBulkUploadTable(false)}
          onConfirm={(data) => {
            setBulkUploadTable(false);
            setMatchData([]);
            setFormDataBulkUpload([]);
            fetchData();
            fetchCardData();
            void fetchMasterProducts();
            void fetchExcelData();

            const skippedEntries = data?.skippedEntries;
            const hasSkipped =
              Array.isArray(skippedEntries) && skippedEntries.length > 0;
            const inserted = Number(data?.inserted ?? 0);
            const skipped = Number(data?.skipped ?? 0);

            if (hasSkipped) {
              setSkippedUploadDialog({
                open: true,
                skippedEntries,
                inserted,
                skipped,
              });
            }
          }}
        />
      )}

      <BulkUploadSkippedDialog
        open={skippedUploadDialog.open}
        skippedEntries={skippedUploadDialog.skippedEntries}
        inserted={skippedUploadDialog.inserted}
        skipped={skippedUploadDialog.skipped}
        downloadFileName="eolTreatmentOfSoldProducts_skipped"
        downloadExcelColumns={EOL_SKIPPED_DOWNLOAD_COLUMNS}
        onClose={() =>
          setSkippedUploadDialog({
            open: false,
            skippedEntries: [],
            inserted: 0,
            skipped: 0,
          })
        }
        // onDownload={(rows) => setExcelData(rows)}
      />
    </>
  );
};

export default EOLTreatmentOfSoldProducts;

